'use client';

import { type ReactNode, useMemo } from 'react';

import type { PostMediaItem } from '@ps/api/types';

import { Slide } from './Slide';
import { useCarousel } from './hooks/useCarousel';
import { CarouselNav } from './nav/CarouselNav';
import { calculatePostMediaAspectRatio } from './utils/mediaAspect';

interface MediaCarouselProps {
  medias: PostMediaItem[];
  /**
   * Index of the first "locked" slide — slides at or after it are
   * blanked-by-restriction in the underlying data. Drives BOTH the internal
   * `paywall` overlay gate and the `onActiveIndexChange` callback.
   */
  unrestrictedLeadingMediaCount?: number;
  /**
   * Fired whenever the visible slide changes — for callers that gate their own UI
   * on the active slide (e.g. the legacy `PostCard`'s paywall CTA). The built-in
   * `paywall` slot already handles the common case. Mirrors legacy
   * `onSlideIndexChange`.
   */
  onActiveIndexChange?: (index: number) => void;
  /**
   * Paywall overlay, shown ONLY while the active slide is past the free-preview
   * window (`unrestrictedLeadingMediaCount`) — so the leading "N open" slides stay
   * viewable and the lock appears once the visitor swipes into gated media. The
   * carousel paints the blanked slides' blurhash preview underneath it.
   */
  paywall?: ReactNode;
  /**
   * Owner surface — each slide shows a "Rejected" overlay when its media was
   * turned down by moderation. Defaults off (visitor). See {@link Slide}.
   */
  editable?: boolean;
  /** Merged onto the carousel root (e.g. the host's sizing / rounding). */
  className?: string;
}

/**
 * The post media carousel — horizontal scroll-snap over `medias[]`, 1:1 with
 * legacy `PostSideshow` in `flows/bio/wall/components/post/slide-view.tsx`. Pure
 * layout: the sliding-show + navigation mechanics (scroll refs, active-slide
 * tracking, tab-blur pause, `scrollToIndex`) live in {@link useCarousel}; this
 * composes the {@link Slide} cells, the {@link CarouselNav} controls, and — for
 * gated posts — a `paywall` overlay stacked over the blurhash preview once the
 * active slide crosses the free-preview window.
 */
export function MediaCarousel({
  medias,
  unrestrictedLeadingMediaCount,
  onActiveIndexChange,
  paywall,
  editable,
  className,
}: MediaCarouselProps) {
  const { scrollerRef, registerSlide, activeIndex, focused, scrollToIndex } =
    useCarousel(medias.length, onActiveIndexChange);

  // Pick one shared aspect ratio for the whole carousel so every slide is the
  // same size — 1:1 with legacy `calculateOptimalHeight` strategy='weighted'.
  // Without this, mixed-orientation posts (a portrait photo next to a landscape
  // video) make the carousel jump in height as the user swipes. The result is
  // clamped so a single extreme aspect doesn't blow out the card.
  const sharedAspectRatio = useMemo(
    () => calculatePostMediaAspectRatio(medias),
    [medias],
  );

  if (medias.length === 0) return null;

  const hasMultiple = medias.length > 1;
  const openCount = unrestrictedLeadingMediaCount ?? 0;
  // Reveal the paywall once the visible slide is inside the gated range.
  const activeSlideGated =
    paywall != null && (openCount <= 0 || activeIndex >= openCount);

  return (
    <div
      // Full column width; rounded corners + dark bg match the legacy slide look.
      className={`relative w-full overflow-hidden rounded-xl bg-black ${className ?? ''}`}
    >
      <div
        ref={scrollerRef}
        // For multi-media posts: horizontal snap-scroll. For single-media posts:
        // no horizontal scroll so a wheel event isn't hijacked from the
        // surrounding post-card vertical scroll.
        className={
          hasMultiple
            ? 'flex w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
            : 'flex w-full overflow-hidden'
        }
      >
        {medias.map((media, index) => (
          <Slide
            key={`${media.id ?? index}-${index}`}
            media={media}
            active={index === activeIndex}
            focused={focused}
            aspectRatio={sharedAspectRatio}
            locked={
              unrestrictedLeadingMediaCount != null &&
              index >= unrestrictedLeadingMediaCount
            }
            editable={editable}
            slideRef={registerSlide(index)}
          />
        ))}
      </div>

      {hasMultiple ? (
        <CarouselNav
          count={medias.length}
          activeIndex={activeIndex}
          onScrollToIndex={scrollToIndex}
        />
      ) : null}

      {activeSlideGated ? paywall : null}
    </div>
  );
}
